import { useState, useMemo } from "react";
import { Shirt } from "lucide-react";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { buildFitOptions } from "@/lib/fit-copy";
import { AssignOptionRow } from "../dialogs/AssignmentDialogShell";
import { BulkAssignmentShell } from "./BulkAssignmentShell";

/**
 * BulkFitDialog — apply a Kalıp value across many selected products at once.
 * Reuses the option-row look from the single-product dialog for visual
 * cohesion; the shell adds the "N ürün · üzerine yaz / sadece boş" banner
 * when at least one selected product already carries a fit value.
 */
export const BulkFitDialog = ({ selectedProducts, onClose }) => {
    const { setProductFit, fitLabels } = useSizing();
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("empty-only");
    const fitOptions = useMemo(() => buildFitOptions(fitLabels), [fitLabels]);
    const conflicts = selectedProducts.filter((p) => !!p.fit);
    const conflictCount = conflicts.length;
    // Human summary of the existing values so merchants know what will get
    // overwritten (e.g. "Normal · 2, Dar · 1"). Falls back gracefully when
    // no labels are set yet.
    const conflictSummary = useMemo(() => {
        const bag = {};
        conflicts.forEach((p) => {
            const label = fitOptions.find((o) => o.storedAs === p.fit)?.label || p.fit;
            bag[label] = (bag[label] || 0) + 1;
        });
        return Object.entries(bag).map(([k, v]) => `${k} · ${v}`).join(", ");
    }, [conflicts, fitOptions]);

    const apply = async (key) => {
        if (saving) return;
        const targets = mode === "empty-only"
            ? selectedProducts.filter((p) => !p.fit)
            : selectedProducts;
        if (targets.length === 0) {
            toast.info("Etkilenecek ürün yok");
            return;
        }
        setSaving(true);
        try {
            // Snapshot the previous fit values so the undo toast can revert
            // each product one-by-one without racing against fresh mutations.
            const snapshot = targets.map((p) => ({ id: p.id, prevFit: p.fit || null }));
            targets.forEach((p) => setProductFit(p.id, key));
            const label = fitOptions.find((o) => o.storedAs === key)?.label || key;
            toast.success(`${targets.length} ürünün kalıbı "${label}" olarak güncellendi`, {
                duration: 5000,
                action: {
                    label: "Geri Al",
                    onClick: () => {
                        snapshot.forEach((s) => setProductFit(s.id, s.prevFit));
                        toast.success(`${snapshot.length} ürünün kalıbı eski haline döndü`);
                    },
                },
            });
            onClose();
        } catch {
            toast.error("Toplu kalıp güncellemesi başarısız");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BulkAssignmentShell
            title="Toplu kalıp ata"
            count={selectedProducts.length}
            conflictCount={conflictCount}
            conflictSummary={conflictSummary}
            mode={mode}
            onModeChange={setMode}
            onClose={onClose}
            testId="bulk-fit-dialog"
        >
            {fitOptions.length === 0 ? (
                <div className="text-center py-10 text-xs text-muted-foreground">
                    Kalıp tanımı yok.
                </div>
            ) : (
                fitOptions.map((o) => (
                    <AssignOptionRow
                        key={o.storedAs}
                        current={false}
                        onClick={() => apply(o.storedAs)}
                        disabled={saving}
                        testId={`bulk-fit-option-${o.storedAs}`}
                    >
                        <div className="h-10 w-10 rounded-md border border-border bg-muted grid place-items-center shrink-0">
                            <Shirt className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{o.label}</div>
                            {o.advice && (
                                <div className="text-[10.5px] text-muted-foreground truncate">{o.advice}</div>
                            )}
                        </div>
                    </AssignOptionRow>
                ))
            )}
        </BulkAssignmentShell>
    );
};
